import { useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "sonner"
import {
	botBuilderKeys,
	deleteScenario,
	type ScenarioDraft,
	updateScenario,
} from "@/entities/bot-builder"
import { Button } from "@/shared/ui/button"
import { useConfirm } from "@/shared/ui/confirm-provider"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/shared/ui/dialog"
import { Field } from "@/shared/ui/form-field"
import { Input } from "@/shared/ui/input"
import { Textarea } from "@/shared/ui/textarea"

export function ScenarioSettingsDialog({
	open,
	onOpenChange,
	draft,
}: {
	open: boolean
	onOpenChange: (open: boolean) => void
	draft: ScenarioDraft
}) {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const { confirm } = useConfirm()
	const [name, setName] = useState(draft.name)
	const [description, setDescription] = useState(draft.description ?? "")
	const [saving, setSaving] = useState(false)

	useEffect(() => {
		if (!open) return
		setName(draft.name)
		setDescription(draft.description ?? "")
	}, [open, draft.name, draft.description])

	const save = async () => {
		setSaving(true)
		try {
			const next = await updateScenario(draft.id, {
				name: name.trim(),
				description: description.trim() || null,
			})
			// Название и описание не входят в ревизию: берём только их, граф в кэше свежее.
			queryClient.setQueryData<ScenarioDraft>(botBuilderKeys.scenario(draft.id), (current) =>
				current ? { ...current, name: next.name, description: next.description } : current,
			)
			await queryClient.invalidateQueries({ queryKey: botBuilderKeys.scenarios })
			onOpenChange(false)
		} catch (error) {
			toast.error(extractApiErrorMessage(error, "Не удалось сохранить сценарий"))
		} finally {
			setSaving(false)
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Сценарий</DialogTitle>
				</DialogHeader>
				<form
					className="space-y-4"
					onSubmit={(event) => {
						event.preventDefault()
						if (name.trim()) void save()
					}}
				>
					<Field label="Название" htmlFor="scenario-settings-name">
						<Input
							id="scenario-settings-name"
							placeholder="Например, Основной сценарий"
							maxLength={100}
							value={name}
							onChange={(event) => setName(event.target.value)}
						/>
					</Field>
					<Field label="Описание" htmlFor="scenario-settings-description">
						<Textarea
							id="scenario-settings-description"
							placeholder="Для чего этот сценарий. Видно только в админке"
							rows={3}
							maxLength={500}
							value={description}
							onChange={(event) => setDescription(event.target.value)}
						/>
					</Field>
					{draft.bots.length > 0 ? (
						<p className="text-xs text-muted-foreground">
							Исполняют: {draft.bots.map((bot) => `@${bot.username}`).join(", ")}
						</p>
					) : null}
					<DialogFooter className="gap-2 sm:justify-between">
						<Button
							type="button"
							variant="ghost"
							className="text-red-400"
							onClick={() =>
								confirm({
									title: `Удалить «${draft.name}»?`,
									message:
										draft.bots.length > 0
											? "Сначала отвяжите сценарий от ботов, иначе удалить не получится."
											: "Черновик и все опубликованные версии удалятся.",
									confirmText: "Удалить",
									onConfirm: async () => {
										try {
											await deleteScenario(draft.id)
										} catch (error) {
											toast.error(extractApiErrorMessage(error, "Не удалось удалить сценарий"))
											return
										}
										queryClient.removeQueries({ queryKey: botBuilderKeys.scenario(draft.id) })
										await queryClient.invalidateQueries({ queryKey: botBuilderKeys.scenarios })
										navigate("/admin/bots", { replace: true })
									},
								})
							}
						>
							Удалить сценарий
						</Button>
						<Button type="submit" disabled={!name.trim() || saving}>
							Сохранить
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	)
}
